import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

function getPageName(pathname: string): string {
  if (pathname === "/" || pathname === "") return "Home";

  const segments = pathname.split("/").filter(Boolean);
  const segment = segments[segments.length - 1];

  const names: Record<string, string> = {
    me: "Profile",
    vault: "Skills Vault",
    mining: "Experience Mining",
    resume: "Resume",
    jobs: "Jobs",
    discover: "Discover",
    swipe: "Swipe",
    tracker: "Tracker",
    settings: "Settings",
    login: "Sign In",
    signup: "Sign Up",
    "forgot-password": "Forgot Password",
    "reset-password": "Reset Password",
    callback: "Authenticating",
    privacy: "Privacy Policy",
    "privacy-public": "Privacy Policy",
    "beta-agreement": "Beta Agreement",
    refresh: "Refresh Status",
    demo: "Demo Usage",
    "quick-import": "Quick Import",
    profile: "Profile",
    philosophies: "Skills Vault",
  };

  return names[segment] ?? segment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Announces SPA route changes to screen readers via an aria-live region.
 * Must be rendered inside a Router context.
 */
export function RouteAnnouncer() {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState("");
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    const pageName = getPageName(location.pathname);
    // No analytics here: PostHogBridge already captures the canonical
    // $pageview for every route change — the old custom page_view event
    // double-counted navigation (and was never in the first-party
    // allowlist, so it only ever landed in PostHog as a duplicate).
    // Clear first so the same-page navigation still triggers a new announcement
    setAnnouncement("");
    const timer = setTimeout(() => {
      setAnnouncement(`Navigated to ${pageName}`);
    }, 50);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
      {announcement}
    </div>
  );
}
